import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, FileText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import ExchangeAnalysisSection, { type ExchangeAnalysisData } from "@/components/ExchangeAnalysisSection";
import BehavioralObservationCard from "@/components/BehavioralObservationCard";
import {
  analyzeBehavioralContinuity,
  fetchRehearsalHistory,
  type BehavioralObservation,
} from "@/lib/behavioralContinuity";
import { useAuth } from "@/contexts/AuthContext";
import ResonantMomentsSection from "@/components/ResonantMomentsSection";
import { detectResonantMoments } from "@/lib/resonantMoments";
import ObservedArchetypesSection from "@/components/ObservedArchetypesSection";
import { detectArchetypes } from "@/lib/archetypes";
import PreviouslyObservedContinuity from "@/components/PreviouslyObservedContinuity";
import { fetchPreviouslyObservedContinuity } from "@/lib/continuity";
import ObservedProgression from "@/components/ObservedProgression";
import { deriveProgression } from "@/lib/progression";
import BehavioralDebriefSections from "@/components/BehavioralDebriefSections";
import { loadDebriefData } from "@/lib/debriefEngine";
import type { TrajectoryState } from "@/lib/behaviorRuntime";
import type { ForecastResponseMode } from "@/lib/forecastEngine";

interface MessageEntry {
  turn: number;
  role: "agent" | "user";
  content: string;
  tactic_tag: string | null;
}

interface TacticEntry {
  turn: number;
  tactic: string;
}

interface PatternEntry {
  name: string;
  score?: number;
  count?: number;
}

const wc = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

// Quiet, observational composure summary — replaces numeric readiness score.
const deriveComposureLine = (messages: MessageEntry[]): string => {
  const userMsgs = messages.filter((m) => m.role === "user");
  if (userMsgs.length === 0) return "Session recorded.";

  const lengths = userMsgs.map((m) => wc(m.content));
  const avg = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const half = Math.floor(userMsgs.length / 2) || 1;
  const earlyAvg = lengths.slice(0, half).reduce((a, b) => a + b, 0) / half;
  const lateAvg = lengths.slice(half).reduce((a, b) => a + b, 0) / Math.max(1, lengths.length - half);

  if (avg <= 8 && lateAvg <= earlyAvg) return "Maintained composure through escalation.";
  if (lateAvg < earlyAvg * 0.7) return "Responses tightened as pressure increased.";
  if (lateAvg > earlyAvg * 1.4) return "Emotional engagement increased after invalidation.";
  if (avg <= 14) return "Remained mostly grounded during pressure cycles.";
  return "Engaged with the simulation under sustained pressure.";
};

// Escalation pattern observation derived from tactic sequence.
const deriveEscalationLine = (
  tactics: TacticEntry[] | null,
  messages: MessageEntry[]
): string | null => {
  const seq = tactics && tactics.length
    ? tactics.map((t) => t.tactic.toLowerCase())
    : messages.filter((m) => m.role === "agent" && m.tactic_tag).map((m) => m.tactic_tag!.toLowerCase());
  if (seq.length < 2) return null;

  const has = (kw: string) => seq.some((s) => s.includes(kw));
  const lateHas = (kw: string) => seq.slice(Math.floor(seq.length / 2)).some((s) => s.includes(kw));

  if (lateHas("darvo") || lateHas("reversal") || lateHas("blame")) return "Emotional reversal emerged after resistance.";
  if (lateHas("invalidat") || lateHas("dismiss")) return "Invalidation repeated during disagreement.";
  if (lateHas("guilt") || lateHas("shame")) return "Guilt induction intensified after boundary signals.";
  if (has("escalat") || has("pressure")) return "Pressure increased after boundary assertion.";
  return null;
};

const RehearsalDebrief = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { sessionId } = useParams<{ sessionId: string }>();
  const rehearsalId = sessionId || (location.state as { rehearsal_id?: string })?.rehearsal_id;

  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<MessageEntry[]>([]);
  const [tacticsSequence, setTacticsSequence] = useState<TacticEntry[] | null>(null);
  const [forensicBaseline, setForensicBaseline] = useState<{ patterns: PatternEntry[] } | null>(null);
  const [observations, setObservations] = useState<BehavioralObservation[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const [exchangeAnalysis, setExchangeAnalysis] = useState<ExchangeAnalysisData | null>(null);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [continuityLines, setContinuityLines] = useState<string[]>([]);
  const [debriefData, setDebriefData] = useState<{
    trajectoryHistory: TrajectoryState[];
    responseModeHistory: ForecastResponseMode[];
  } | null>(null);

  useEffect(() => {
    if (!rehearsalId) return;
    setDebriefData(loadDebriefData(rehearsalId));
  }, [rehearsalId]);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("check-admin");
        setIsAdmin(data?.is_admin === true);
      } catch { setIsAdmin(false); }
    })();
  }, [user]);

  useEffect(() => {
    if (!rehearsalId) return;
    (async () => {
      const { data, error } = await supabase
        .from("rehearsal_sessions")
        .select("messages, tactics_sequence, forensic_baseline, exchange_analysis")
        .eq("id", rehearsalId)
        .maybeSingle();

      if (error || !data) {
        setLoading(false);
        return;
      }

      setMessages((data.messages as unknown as MessageEntry[]) || []);
      setTacticsSequence((data.tactics_sequence as unknown as TacticEntry[]) || null);
      setForensicBaseline(data.forensic_baseline as unknown as { patterns: PatternEntry[] } | null);

      if (data.exchange_analysis) {
        setExchangeAnalysis(data.exchange_analysis as unknown as ExchangeAnalysisData);
      }
      setLoading(false);
    })();
  }, [rehearsalId]);

  useEffect(() => {
    if (loading || !rehearsalId || !user || messages.length < 2) return;
    (async () => {
      try {
        const history = await fetchRehearsalHistory(user.id, rehearsalId, 10);
        const current = {
          id: rehearsalId,
          created_at: new Date().toISOString(),
          entry_type: (location.state as { entry_type?: string })?.entry_type || "personal",
          messages: messages as never,
          tactics_sequence: tacticsSequence as never,
        };
        const obs = analyzeBehavioralContinuity(current as never, history);
        setObservations(obs);
      } catch (e) {
        console.error("Continuity analysis error:", e);
      }
    })();

    fetchPreviouslyObservedContinuity(user.id, rehearsalId, messages, tacticsSequence || [])
      .then(setContinuityLines)
      .catch(() => {});
  }, [loading, rehearsalId, user, messages, tacticsSequence, location.state]);

  useEffect(() => {
    if (loading || !rehearsalId || exchangeAnalysis || messages.length === 0) return;
    const fetchAnalysis = async () => {
      setExchangeLoading(true);
      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke("analyze-rehearsal", {
          body: { rehearsal_id: rehearsalId },
        });
        if (fnError) throw fnError;
        if (fnData?.error) throw new Error(fnData.error);
        if (fnData?.exchange_analysis) {
          setExchangeAnalysis(fnData.exchange_analysis as ExchangeAnalysisData);
        }
      } catch (e) {
        console.error("Exchange analysis error:", e);
        setExchangeError("Forensic analysis unavailable. Try again later.");
      } finally {
        setExchangeLoading(false);
      }
    };
    fetchAnalysis();
  }, [loading, rehearsalId, exchangeAnalysis, messages.length]);

  if (!rehearsalId) return <Navigate to="/analysis-result" replace />;

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const composureLine = deriveComposureLine(messages);
  const escalationLine = deriveEscalationLine(tacticsSequence, messages);
  const archetypes = detectArchetypes(messages, tacticsSequence);
  const progression = deriveProgression(messages, tacticsSequence);
  const moments = detectResonantMoments(messages, 3);
  const primaryMoment = moments[0];
  const patternNames = forensicBaseline?.patterns?.map((p) => p.name) || [];
  const exchangeCount = messages.filter((m) => m.role === "user").length;

  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-border/50">
        <button
          onClick={() => navigate("/analysis-result")}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-sm font-mono tracking-[0.18em] uppercase text-foreground/80">
          Rehearsal Debrief
        </h1>
      </div>

      <div className="max-w-xl mx-auto px-5 py-10 space-y-12">
        {/* STAGE 1 — SESSION COMPLETE */}
        <section className="text-center space-y-4 animate-fadeIn">
          <p className="text-[10px] font-mono tracking-[0.32em] uppercase text-cyan/70">
            Session Complete
          </p>
          <p className="text-lg text-foreground/90 font-light leading-relaxed max-w-md mx-auto">
            {composureLine}
          </p>
          {escalationLine && (
            <p className="text-xs text-muted-foreground/70 font-light italic max-w-sm mx-auto">
              {escalationLine}
            </p>
          )}
        </section>

        {/* Subtle divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />

        {/* BEHAVIORAL DEBRIEF V2 — trajectory analysis layer */}
        {debriefData && debriefData.trajectoryHistory.length > 0 && (
          <BehavioralDebriefSections
            trajectoryHistory={debriefData.trajectoryHistory}
            responseModeHistory={debriefData.responseModeHistory}
          />
        )}

        {/* STAGE 2 — OBSERVED ARCHETYPES */}
        {archetypes.length > 0 && (
          <ObservedArchetypesSection detected={archetypes} />
        )}

        {/* STAGE 2.5 — OBSERVED PROGRESSION (cinematic sequence) */}
        {progression && <ObservedProgression progression={progression} />}

        {/* STAGE 3 — KEY OBSERVATION (resonant moment, primary) */}
        {primaryMoment && (
          <section id="resonant-moments-anchor" className="space-y-3 scroll-mt-24">
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-cyan/60" />
              <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/70">
                Resonant Moment Observed
              </p>
            </div>
            <div className="rounded-xl border border-cyan/15 bg-card/30 backdrop-blur-sm p-5 space-y-2">
              <p className="text-base text-foreground/90 font-light italic leading-relaxed">
                "{primaryMoment.quote}"
              </p>
              <p className="text-[11px] text-muted-foreground/60 font-light">
                {primaryMoment.observation}
              </p>
            </div>
          </section>
        )}

        {/* Behavioral continuity (cross-session) — quiet */}
        {observations.length > 0 && (
          <BehavioralObservationCard observations={observations} />
        )}

        {/* Additional resonant moments (secondary, collapsed feel) */}
        {moments.length > 1 && (
          <ResonantMomentsSection moments={moments.slice(1)} />
        )}

        {/* STAGE 4 — OPTIONAL DEEP ANALYSIS */}
        {exchangeLoading && <ExchangeAnalysisSection type="loading" />}
        {exchangeError && <ExchangeAnalysisSection type="error" message={exchangeError} />}
        {exchangeAnalysis && <ExchangeAnalysisSection type="loaded" data={exchangeAnalysis} />}

        {/* PREVIOUSLY OBSERVED CONTINUITY — cross-session memory */}
        {continuityLines.length > 0 && (
          <PreviouslyObservedContinuity lines={continuityLines} />
        )}

        {/* Subtle divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />

        {/* STAGE 5 — NEXT ACTIONS */}
        <section className="space-y-2.5">
          <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/55 text-center pb-1">
            Continue
          </p>
          <Button
            variant="outline"
            className="w-full rounded-xl border-border/50 bg-card/40 hover:bg-card/70 font-light"
            onClick={() => navigate("/practice")}
          >
            <RotateCcw className="w-4 h-4 mr-2 opacity-70" />
            Practice Again
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              className="w-full rounded-xl border-border/50 bg-card/40 hover:bg-card/70 font-light"
              onClick={() => {
                navigate("/admin/content-library", {
                  state: {
                    openScriptFactory: true,
                    rehearsalData: {
                      rehearsal_id: rehearsalId,
                      tactics: patternNames.slice(0, 3),
                      exchange_count: exchangeCount,
                    },
                    initialCardType: "rehearsal",
                  },
                });
              }}
            >
              <FileText className="w-4 h-4 mr-2 opacity-70" />
              Turn Into Content
            </Button>
          )}
          {primaryMoment && (
            <Button
              variant="ghost"
              className="w-full rounded-xl text-muted-foreground/70 hover:text-foreground font-light"
              onClick={() => {
                const el = document.getElementById("resonant-moments-anchor");
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            >
              <Sparkles className="w-4 h-4 mr-2 opacity-60" />
              Preserve Resonant Moment
            </Button>
          )}
        </section>
      </div>
    </div>
  );
};

export default RehearsalDebrief;
